/** @module One capture run: start it, upload photos, receive results live. */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, problemFrom } from "@/api/client";
import {
  type CaptureInfo,
  enqueue,
  markAttempt,
  pending,
  remove,
} from "@/features/scan/scan-queue";
import { APP_VERSION } from "@/lib/app-version";
import { log } from "@/lib/client-log";
import { accessToken } from "@/lib/session";
import { streamEvents } from "@/lib/sse";

// How often unfinished scans are checked when the event stream stays quiet.
const POLL_MS = 4000;

export type ScanState = "queued" | "processing" | "done" | "failed";

export interface ScanEntry {
  key: string;
  scanId: string | null;
  state: ScanState;
  decision?: string;
  printId?: string | null;
  thumbnail: string;
  error?: string;
  /** The photo looks like the one before it: an accidental double or a second copy. */
  repeat?: boolean;
  /** Booked from the correction sheet, without leaving the camera. */
  booked?: boolean;
}

export interface SessionOptions {
  language: string;
  setHint?: string | null;
  /** Keep the photos beyond the retention period, for measuring the recognition. */
  benchmark?: boolean;
  /** Booked cards go into this storage location. */
  location?: string | null;
}

async function createSession(options: SessionOptions): Promise<string> {
  const { data, response } = await api.POST("/api/v1/scan-sessions", {
    body: {
      default_language: options.language,
      set_hint: options.setHint ?? null,
      benchmark_opt_in: options.benchmark ?? true,
      condition_tags: [],
      location_id: options.location ?? null,
    },
  });
  if (!data) throw problemFrom(response);
  return data.id;
}

export function useScanSession(options: SessionOptions) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ScanEntry[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  // State is not readable between two awaits in the same loop, so the session lives
  // in a promise: picking 30 files must not create 30 sessions.
  const sessionPromise = useRef<Promise<string> | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const update = useCallback((key: string, patch: Partial<ScanEntry>) => {
    setEntries((current) =>
      current.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)),
    );
  }, []);

  const ensureSession = useCallback(async (): Promise<string | null> => {
    sessionPromise.current ??= createSession(options).then((id) => {
      setSessionId(id);
      return id;
    });
    try {
      return await sessionPromise.current;
    } catch (cause) {
      sessionPromise.current = null;
      setError(cause instanceof Error ? cause.message : "unknown");
      return null;
    }
    // The options are read once per session on purpose; changing them starts a new one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.language, options.setHint, options.benchmark, options.location]);

  /** Send everything the queue holds; a failed upload stays for the next attempt. */
  const drain = useCallback(async () => {
    if (!navigator.onLine) return;
    for (;;) {
      const batch = await pending();
      if (batch.length === 0) return;
      let sent = 0;
      for (const entry of batch) {
        const form = new FormData();
        form.append("photo", entry.photo, "scan.jpg");
        if (entry.quad) form.append("quad", JSON.stringify(entry.quad));
        if (entry.capture) form.append("capture", JSON.stringify(entry.capture));
        const token = accessToken();
        const response = await fetch(`/api/v1/scan-sessions/${entry.sessionId}/scans`, {
          method: "POST",
          body: form,
          headers: {
            "Idempotency-Key": entry.key,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (response.ok) {
          const body = (await response.json()) as { id: string; status: ScanState };
          update(entry.key, { scanId: body.id, state: body.status });
          await remove(entry.key);
          sent += 1;
        } else if (response.status >= 400 && response.status < 500) {
          // The server will never accept this photo; keeping it would block the queue.
          const body: unknown = await response.json().catch(() => null);
          const code =
            body && typeof body === "object" && "code" in body ? String(body.code) : "rejected";
          update(entry.key, { state: "failed", error: code });
          await remove(entry.key);
          sent += 1;
        } else {
          log("scan.upload_failed", { status: response.status }, "warning");
          await markAttempt(entry);
          return;
        }
      }
      // Nothing moved: the server is unhappy with all of them, so stop for now.
      if (sent === 0) return;
    }
  }, [update]);

  /**
   * Uploads run one after another in a chain.
   *
   * Picking 30 files calls this 30 times within a few milliseconds; a plain "already
   * running" guard would drop those wake-ups and leave photos in the queue.
   */
  const flush = useCallback(() => {
    queue.current = queue.current.then(drain, drain);
    return queue.current;
  }, [drain]);

  const capture = useCallback(
    async (photo: Blob, hint?: number[][], repeat = false, info: CaptureInfo = {}) => {
      log("scan.capture_start", { bytes: photo.size });
      const id = await ensureSession();
      if (!id) {
        log("scan.capture_no_session", {}, "error");
        return;
      }
      const entry = await enqueue(id, photo, hint, {
        ...info,
        repeat,
        app_version: APP_VERSION,
      });
      setEntries((current) => [
        {
          key: entry.key,
          scanId: null,
          state: "queued",
          thumbnail: URL.createObjectURL(photo),
          repeat,
        },
        ...current,
      ]);
      log("scan.capture_queued", { key: entry.key });
      await flush();
      log("scan.capture_done", { key: entry.key });
    },
    [ensureSession, flush],
  );

  // Live results; the stream also tells us when a scan failed in the worker.
  useEffect(() => {
    if (!sessionId) return;
    const controller = new AbortController();
    void streamEvents(
      `/api/v1/scan-sessions/${sessionId}/events`,
      (event) => {
        const payload = event.data as {
          scan_id?: string;
          status?: ScanState;
          decision?: string;
          print_id?: string | null;
        };
        if (!payload.scan_id) return;
        setEntries((current) =>
          current.map((entry) =>
            entry.scanId === payload.scan_id
              ? {
                  ...entry,
                  state: payload.status ?? entry.state,
                  decision: payload.decision,
                  printId: payload.print_id,
                }
              : entry,
          ),
        );
      },
      controller.signal,
    );
    return () => {
      controller.abort();
    };
  }, [sessionId]);

  /**
   * Ask for anything the stream did not deliver.
   *
   * A proxy that buffers or a dropped connection would otherwise leave photos on
   * "working" forever, even though the server finished them long ago.
   */
  useEffect(() => {
    const open = entries.filter(
      (entry) => entry.scanId !== null && entry.state !== "done" && entry.state !== "failed",
    );
    if (open.length === 0) return;

    const timer = setInterval(() => {
      void (async () => {
        for (const entry of open) {
          if (!entry.scanId) continue;
          const { data } = await api.GET("/api/v1/scans/{scan_id}", {
            params: { path: { scan_id: entry.scanId } },
          });
          if (!data || data.status === "queued" || data.status === "processing") continue;
          update(entry.key, {
            state: data.status,
            decision: data.result?.decision,
            printId: data.result?.chosen_print_id,
            error: data.error ?? undefined,
          });
        }
      })();
    }, POLL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [entries, update]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void flush();
    };
    const goOffline = () => {
      setOnline(false);
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [flush]);

  /**
   * Delete an uploaded scan for good: the photo, its result and any card it booked.
   *
   * A table or a finger must not stay on the server; true when it is gone.
   */
  const removeScan = useCallback(
    async (key: string): Promise<boolean> => {
      const entry = entries.find((candidate) => candidate.key === key);
      if (!entry?.scanId) return false;
      const { response } = await api.DELETE("/api/v1/scans/{scan_id}", {
        params: { path: { scan_id: entry.scanId } },
      });
      // Gone already counts as done; "still working" keeps it for another try.
      if (!response.ok && response.status !== 404) return false;
      URL.revokeObjectURL(entry.thumbnail);
      setEntries((current) => current.filter((candidate) => candidate.key !== key));
      log("scan.deleted", { repeat: entry.repeat ?? false });
      return true;
    },
    [entries],
  );

  /** End this run: the next photo starts a new session with the options then in force. */
  const restart = useCallback(() => {
    sessionPromise.current = null;
    setSessionId(null);
    setEntries([]);
  }, []);

  return {
    sessionId,
    entries,
    online,
    error,
    capture,
    flush,
    remove: removeScan,
    restart,
    settle: update,
  };
}
